import React from 'react';
import { Box, Button, Divider, TextField, Typography } from '@mui/material';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { MuiModal } from 'ui/MuiModal';
import { isDefined } from 'lib/typeguards';

interface ActionDialogProps extends ActionDialogContentProps {
    open: boolean;
    protectionWord?: string;
}

export function ActionDialog(props: ActionDialogProps) {
    const { open, protectionWord, ...rest } = props;
    if (isDefined(protectionWord)) {
        return (
            <MuiModal open={open} onClose={rest.onClose} paperMaxWidth={720}>
                <ProtectedActionDialogContent protectionWord={protectionWord} {...rest} />
            </MuiModal>
        );
    }
    return (
        <MuiModal open={open} onClose={rest.onClose}>
            <ActionDialogContent {...rest} />
        </MuiModal>
    );
}

interface ActionDialogContentProps {
    title: React.ReactNode;
    content: React.ReactNode;
    submitLabel?: string;
    discardLabel?: string;
    onClose: () => void;
    loading?: boolean;
    onConfirm: () => void;
}
function ActionDialogContent({
    title,
    content,
    onClose,
    loading,
    onConfirm,
    submitLabel,
    discardLabel
}: ActionDialogContentProps) {
    return (
        <React.Fragment>
            <Typography variant="h5" color="primary">
                {title}
            </Typography>
            <Divider sx={{ my: 2, mx: 0 }} />
            <Typography variant="body1">{content}</Typography>
            <Divider sx={{ my: 2, mx: 0 }} />
            <Box justifyContent="flex-end" sx={{ width: '100%', display: 'flex' }}>
                <Button sx={{ mr: 2.25 }} variant="outlined" color="primary" onClick={onClose}>
                    {discardLabel || 'Cancel'}
                </Button>
                <LoadingButton
                    loading={loading}
                    disabled={loading}
                    variant="contained"
                    color="primary"
                    onClick={onConfirm}
                >
                    {submitLabel || 'Delete'}
                </LoadingButton>
            </Box>
        </React.Fragment>
    );
}

interface ProtectedActionDialogProps extends ActionDialogContentProps {
    protectionWord: string;
}

// TODO: why do we have such a component? Cannot we reduce the amount of code?
function ProtectedActionDialogContent({
    title,
    content,
    onClose,
    loading,
    onConfirm,
    submitLabel,
    discardLabel,
    protectionWord
}: ProtectedActionDialogProps) {
    const [inputValue, setInputValue] = React.useState('');

    const handleInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    }, []);

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter' && !loading && inputValue === protectionWord) {
                onConfirm();
            }
        },
        [loading, onConfirm, inputValue, protectionWord]
    );

    return (
        <React.Fragment>
            <Typography variant="h5" color="primary">
                {title}
            </Typography>
            <Divider sx={{ my: 2, mx: 0 }} />
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {content}
            </Typography>
            <TextField
                fullWidth
                autoFocus
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
            />
            <Divider sx={{ my: 2, mx: 0 }} />
            <Box justifyContent="flex-end" sx={{ width: '100%', display: 'flex' }}>
                <Button sx={{ mr: 2.25 }} variant="outlined" color="primary" onClick={onClose}>
                    {discardLabel || 'Cancel'}
                </Button>
                <LoadingButton
                    loading={loading}
                    disabled={loading || inputValue !== protectionWord}
                    variant="contained"
                    color="primary"
                    onClick={onConfirm}
                >
                    {submitLabel || 'Delete'}
                </LoadingButton>
            </Box>
        </React.Fragment>
    );
}
